import { useParams } from 'react-router-dom'

import { AvatarStack, Box } from '@factorialco/deprecated-design-system'
import { ItemAssigneesDocument } from 'generated/resources/graphql'
import { capitalize } from 'lodash'

import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'

import { ProcessCategories } from 'modules/processes/domain'
import { assigneeSelectionTypes } from 'modules/processes/lib/assigneeTypeSubOptions'

import { AssigneeTypeSubOptions } from './AssigneeTypeSubOptions'
import { AssigneeStrategy, RoleTypes } from './types'

interface Props {
  count?: number | null
  readonly?: boolean
  accessIds: string[] | undefined
  itemAssigneeIds?: string[]
  assigneeStrategy: AssigneeStrategy | undefined
}

const ItemAssignees = ({ count = 0, accessIds, assigneeStrategy, itemAssigneeIds }: Props) => {
  const { category } = useParams()
  const assignees = useAssigneeTypeAndStrategy(
    assigneeStrategy,
    accessIds,
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    category as ProcessCategories,
    itemAssigneeIds
  ) ?? [{ label: '', url: '' }]

  return (
    <Box alignItems="center" flexDirection="row" gap="s12">
      <ItemAssigneesList totalCount={count ?? 0} itemAssignees={assignees} />
    </Box>
  )
}

interface ItemAssigneesListProps {
  totalCount: number
  itemAssignees: { label: string; url: string }[]
}

const ItemAssigneesList = ({ totalCount, itemAssignees }: ItemAssigneesListProps) => {
  const assigneesLimit = 3
  const countLimit = 99
  const count = totalCount - assigneesLimit
  const leftover = Math.min(count, countLimit)

  const avatars = itemAssignees.slice(0, assigneesLimit)

  return (
    <Box gap="s8" alignItems="center" flexDirection="row">
      <AvatarStack
        size="s32"
        overlap="s12"
        textColor="viridian1000"
        textSize="200"
        backgroundColor="viridian200"
        avatars={avatars}
        leftover={leftover}
      />
    </Box>
  )
}

function useAssigneeTypeAndStrategy(
  assigneeType: AssigneeStrategy | RoleTypes | undefined,
  accessIds: string[] | undefined,
  category: ProcessCategories,
  itemAssigneeIds?: string[]
) {
  let strategyName = ''
  let itemAssignees = null

  const shouldSkipQuery = !accessIds || assigneeType !== AssigneeStrategy.MANUAL_SELECTION

  const { data } = useQuery(ItemAssigneesDocument, {
    variables: { first: 3, ids: accessIds ?? [] },
    skip: shouldSkipQuery,
  })

  const accesses = data?.apiCore.accessesConnection.nodes

  switch (assigneeType) {
    case AssigneeStrategy.MANUAL_SELECTION:
      itemAssignees = accesses
        ?.filter((access) => accessIds?.includes(access.id))
        .map((access) => ({
          label: access.fullName,
          url: buildUrl(access.avatar),
        }))
      break
    case RoleTypes.MANAGER:
      strategyName = capitalize(RoleTypes.MANAGER)
      break
    case AssigneeStrategy.WORKSPACE:
      strategyName = getOptionLabel(AssigneeStrategy.WORKSPACE, 'Unknown Workspace', accessIds)
      break
    case AssigneeStrategy.TEAMS:
      strategyName = getOptionLabel(AssigneeStrategy.TEAMS, 'Unknown Team', itemAssigneeIds)
      break
    default:
      strategyName = ''
      break
  }

  const assigneTypeLabel = assigneeSelectionTypes(category).find(
    (option) => option.value === assigneeType
  )
  const assigneeTypeAndStrategy = assigneTypeLabel
    ? `${assigneTypeLabel?.label} ${strategyName}`
    : strategyName
  return itemAssignees ?? [{ label: assigneeTypeAndStrategy, url: '' }]
}

export default ItemAssignees

type SubOptionType = AssigneeStrategy.WORKSPACE | AssigneeStrategy.TEAMS

const getOptionLabel = (strategyType: SubOptionType, emptyLabel: string, accessIds?: string[]) => {
  const option = AssigneeTypeSubOptions[strategyType](accessIds || []).find((op) =>
    accessIds?.includes(op.value)
  )
  return option?.label ?? emptyLabel
}
